import { useMutation } from '@tanstack/react-query'
import { useEffect } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useRouteSession } from '@web/app'
import { authClient } from '@web/auth'
import { Alert, AlertDescription } from '@layerit/ui/components/ui/alert'
import { useOrganizations } from '@web/hooks/organizations'
import { ROUTES } from '@web/router'
import { CreateOrganisationForm } from '@web/screens/organisations/create-form'
import { OnboardingShell } from '@web/screens/onboarding/shell'

export function OrganisationScreen() {
	const { reload } = useRouteSession()
	const navigate = useNavigate()
	const organizations = useOrganizations()

	const invitationId = typeof window === 'undefined' ? null : new URLSearchParams(window.location.search).get('invitation')

	const accept = useMutation({
		mutationFn: async (id: string) => authClient.organization.acceptInvitation({ invitationId: id }),
		onSuccess: async (result) => {
			if (result.error) return
			await reload()
			await navigate(ROUTES.getStarted, { replace: true })
		},
	})

	useEffect(() => {
		if (!invitationId || accept.isPending || accept.isSuccess || accept.isError) return
		accept.mutate(invitationId)
	}, [invitationId, accept])

	if (organizations.isSuccess && organizations.data.length > 0) {
		return <Navigate to={ROUTES.chooseOrganisation} replace />
	}

	if (organizations.isPending || organizations.isError) return null

	return (
		<OnboardingShell
			step={1}
			title="Where do you work?"
			description="Every template, brand kit and connection lives inside one organisation, so the console needs to know which is yours. You can be in more than one."
		>
			{accept.isError && (
				<Alert variant="destructive" className="mb-6">
					<AlertDescription>
						That invitation could not be accepted. It may have expired, or have been sent to another address.
					</AlertDescription>
				</Alert>
			)}

			{accept.isPending && (
				<p role="status" className="mb-6 text-sm text-ink-3">
					Joining the organisation you were invited to…
				</p>
			)}

			<CreateOrganisationForm />
		</OnboardingShell>
	)
}
